import AppContext from "PFApp/app_context";
import { AvailabilityContent, AvailabilityContext } from "PFApp/profiles/common/availability";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Option } from "PFComponents/multi_toggle/multi_toggle";
import { HeadlineTabs } from "PFComponents/tabs/headline_tabs";
import { useBookingApprovalsInvalidate } from "PFCore/hooks/queries/bookings/approvals/use_booking_approvals_invalidate";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { Profile } from "PFTypes";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useBookingModalContext } from "../../../booking/components/booking_modal";
import { BookingModalMode } from "../../../booking/components/booking_modal/use_booking_modal";
import Legend from "../../../booking/parts/overview/legend";
import ViewInBookingModuleButton from "../../../components/view_in_booking_module_button/view_in_booking_module_button";
import useIsAvailabilityPermitted from "../../../use_is_availability_permitted";
import { useProfileBookingsContext } from "../../common/profile_bookings_provider";
import css from "./availability_section.module.scss";
import PageSection from "./page_section";

const AvailabilityLegend = () => (
  <Legend theme={css} display={{ warnings: false, engagements: false, roles: false }} />
);

type HeaderToggleType = "calendar" | "list";

type AvailabilitySectionProps = {
  profile: Profile;
  isLoading: boolean;
  className: string;
  style: React.CSSProperties;
  isAdmin: boolean;
  isMe: boolean;
  onExpand: () => void;
  isExpanded: boolean;
  shouldHideExpandAction: boolean;
  myRef: React.RefObject<HTMLDivElement>;
};

const AvailabilitySection = ({
  profile,
  isLoading,
  className,
  style,
  isAdmin,
  isMe,
  onExpand,
  isExpanded,
  shouldHideExpandAction,
  myRef
}: AvailabilitySectionProps): JSX.Element | null => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation(["profiles", "availability"]);
  const { modal } = useBookingModalContext();
  const { bookingCategories } = useBookingCategories();
  const { refreshBookings } = useProfileBookingsContext();
  const { invalidate: invalidateBookingApprovals } = useBookingApprovalsInvalidate();
  const handleBookingsUpdate = () => {
    invalidateBookingApprovals();
    refreshBookings();
  };
  const { canProfileCreateBooking } = useBookingModulePermissions();
  const canSeeBookingModule = canProfileCreateBooking(profile.id);

  const readOnly = !isMe;

  /* @ts-ignore */
  const canEditAvailability = useIsAvailabilityPermitted(profile, ["rw"]) && !readOnly;

  const [activeTab, setActiveTab] = useState<HeaderToggleType>("calendar");

  const getProfileFieldsAccessLevel = (profile, key) => {
    try {
      const pgId = profile.permissions_group.id;
      const pg = currentProfile.permissions_group.cross_group_permissions[pgId];
      return pg.profile_fields_access_levels[key];
    } catch {
      return null;
    }
  };

  const options = useMemo<Option<HeaderToggleType>[]>(
    () => [
      {
        id: "calendar",
        value: <Icon name="calendar" size="sm" />
      },
      { id: "list", value: <Icon name="list" size="sm" /> }
    ],
    []
  );

  if (!getProfileFieldsAccessLevel(profile, "availability")) {
    return null;
  }

  const hasWriteAccessToAtLeastOneCategory = bookingCategories.some((i) => !i.readonly);

  const sectionHeader = (
    <HeadlineTabs<HeaderToggleType>
      label={t("profiles:show.parts.availability")}
      options={options}
      onChange={(value) => {
        setActiveTab(value);
      }}
      value={activeTab}
      classes={{ label: css.availabilityHeadline }}
    >
      {!shouldHideExpandAction && (
        <Button kind="blank" onClick={onExpand}>
          <Icon name={isExpanded ? "collapse" : "expand"} size="sm" />
        </Button>
      )}
      {canSeeBookingModule && <ViewInBookingModuleButton workforceMemberId={profile.id} />}
      {canEditAvailability && (
        <Button
          kind="primary"
          onClick={() =>
            modal.open(
              { onSubmit: handleBookingsUpdate, profileId: profile.id },
              { mode: BookingModalMode.Create }
            )
          }
          disabled={isLoading || !hasWriteAccessToAtLeastOneCategory}
        >
          {t("availability:actions.book")}
        </Button>
      )}
    </HeadlineTabs>
  );

  return (
    <PageSection
      header={sectionHeader}
      footer={<AvailabilityLegend />}
      style={style}
      className={className}
      contentStyle={{ overflow: "auto" }}
    >
      <div ref={myRef} />
      <AvailabilityContent
        profile={profile}
        isLoading={isLoading}
        activeViews={[activeTab]}
        isAdmin={isAdmin}
        readOnly={readOnly}
        isExpanded={isExpanded}
      />
    </PageSection>
  );
};

export const AvailabilitySectionWrapper = (props: AvailabilitySectionProps): JSX.Element => {
  const { isAdmin, profile } = props;
  return (
    <AvailabilityContext isAdmin={isAdmin} profile={profile}>
      <AvailabilitySection {...props} />
    </AvailabilityContext>
  );
};
